import {
  ApartmentRounded,
  ArrowForwardRounded,
  DevicesRounded,
  ElderlyRounded,
  PeopleAltRounded,
  RocketLaunchRounded,
} from "@mui/icons-material";
import { Alert, Box, Button, ButtonBase, Chip, CircularProgress, Paper, Stack, Typography } from "@mui/material";
import type { ReactElement } from "react";
import { useEffect, useMemo, useState } from "react";
import { ListPaginationContext, Pagination, useGetIdentity, useGetList, useRedirect, useTranslate } from "react-admin";

import type { UserIdentity } from "../auth/lib";
import { ResourceName } from "../constants/resources";
import { useLocationFilterContext } from "../hooks/useLocationFilterContext";
import { useOrganizationContext } from "../hooks/useOrganizationContext";
import { borderRadius, color, deviceStatusStyle, shadows, space, typography } from "../theme/designTokens";
import type { DeviceRecord, DeviceStatus } from "../types/device";
import type { LocationRecord } from "../types/location";
import type { PatientRecord } from "../types/patient";
import { PRACTITIONER_LOCATION_SCOPE, type PractitionerRecord } from "../types/practitioner";

type DashboardView = "overview" | "practitioners" | "patients" | "devices";
type DashboardLocationRecord = Omit<LocationRecord, "externalId">;

type DashboardRow = {
  badge?: string;
  badgeColor?: string;
  badgeBackground?: string;
  icon: ReactElement;
  iconBackground: string;
  id: string;
  meta: string;
  resource: ResourceName;
  subtitle: string;
  title: string;
};

type DashboardQuery<T> = {
  data?: T[];
  error?: Error | null;
  isPending: boolean;
  meta?: { isPartial?: boolean };
  total?: number;
};

const DASHBOARD_PAGE_SIZE = 5;

const firstNameFrom = (identity?: UserIdentity): string | undefined =>
  identity?.firstName || identity?.fullName?.trim().split(/\s+/)[0] || undefined;

const patientLocationName = (patient: PatientRecord): string | undefined => {
  const hierarchy = patient.locationHierarchy || [];
  return hierarchy[hierarchy.length - 1]?.locationName;
};

export const HomeDashboard = () => {
  const translate = useTranslate();
  const redirect = useRedirect();
  const { data: identityData } = useGetIdentity();
  const identity = identityData as UserIdentity | undefined;
  const { organizationId, isLoading: isOrganizationLoading } = useOrganizationContext();
  const { locationId } = useLocationFilterContext();
  const [activeView, setActiveView] = useState<DashboardView>("overview");
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(DASHBOARD_PAGE_SIZE);

  const selectView = (view: DashboardView) => {
    if (view === activeView) return;
    setPage(1);
    setActiveView(view);
  };

  const changePerPage = (nextPerPage: number) => {
    setPage(1);
    setPerPage(nextPerPage);
  };

  const queriesEnabled = !isOrganizationLoading && !!organizationId;
  const commonFilter = { organizationId, locationId };
  const commonParams = {
    pagination: { page, perPage },
    sort: { field: "name", order: "ASC" as const },
  };

  useEffect(() => {
    setPage(1);
  }, [locationId, organizationId]);

  const locationCountQuery = useGetList<LocationRecord>(
    ResourceName.Location,
    {
      pagination: { page: 1, perPage: 0 },
      sort: { field: "name", order: "ASC" },
      filter: commonFilter,
      meta: { dashboardCountOnly: true },
    },
    { enabled: queriesEnabled && activeView !== "overview" }
  ) as DashboardQuery<LocationRecord>;
  const practitionerCountQuery = useGetList<PractitionerRecord>(
    ResourceName.Practitioner,
    { ...commonParams, pagination: { page: 1, perPage: 0 }, filter: commonFilter, meta: { dashboardCountOnly: true } },
    { enabled: queriesEnabled && activeView !== "practitioners" }
  ) as DashboardQuery<PractitionerRecord>;
  const patientCountQuery = useGetList<PatientRecord>(
    ResourceName.Patient,
    { ...commonParams, pagination: { page: 1, perPage: 0 }, filter: commonFilter, meta: { dashboardCountOnly: true } },
    { enabled: queriesEnabled && activeView !== "patients" }
  ) as DashboardQuery<PatientRecord>;
  const deviceCountQuery = useGetList<DeviceRecord>(
    ResourceName.Device,
    { ...commonParams, pagination: { page: 1, perPage: 0 }, filter: commonFilter, meta: { dashboardCountOnly: true } },
    { enabled: queriesEnabled && activeView !== "devices" }
  ) as DashboardQuery<DeviceRecord>;
  /*
   * Start uses scoped dashboard-row queries with their own cache identity;
   * the sidebar opens the regular resource lists. The perceived latency
   * difference between those paths has not been measured.
   */
  const allLocations = useGetList<DashboardLocationRecord>(
    ResourceName.Location,
    {
      ...commonParams,
      filter: commonFilter,
      meta: { dashboardRows: true },
    },
    { enabled: queriesEnabled && activeView === "overview" }
  ) as DashboardQuery<DashboardLocationRecord>;
  const practitioners = useGetList<PractitionerRecord>(
    ResourceName.Practitioner,
    { ...commonParams, filter: commonFilter, meta: { dashboardRows: true } },
    { enabled: queriesEnabled && activeView === "practitioners" }
  ) as DashboardQuery<PractitionerRecord>;
  const patients = useGetList<PatientRecord>(
    ResourceName.Patient,
    { ...commonParams, filter: commonFilter, meta: { dashboardRows: true } },
    { enabled: queriesEnabled && activeView === "patients" }
  ) as DashboardQuery<PatientRecord>;
  const devices = useGetList<DeviceRecord>(
    ResourceName.Device,
    { ...commonParams, filter: commonFilter, meta: { dashboardRows: true } },
    { enabled: queriesEnabled && activeView === "devices" }
  ) as DashboardQuery<DeviceRecord>;
  const locationTotal = activeView === "overview" ? allLocations : locationCountQuery;
  const practitionerTotal = activeView === "practitioners" ? practitioners : practitionerCountQuery;
  const patientTotal = activeView === "patients" ? patients : patientCountQuery;
  const deviceTotal = activeView === "devices" ? devices : deviceCountQuery;
  const counts = {
    locations: locationTotal.total || 0,
    practitioners: practitionerTotal.total || 0,
    patients: patientTotal.total || 0,
    devices: deviceTotal.total || 0,
  };
  const activeQuery =
    activeView === "overview"
      ? allLocations
      : activeView === "practitioners"
        ? practitioners
        : activeView === "patients"
          ? patients
          : devices;
  const isPending = isOrganizationLoading || (queriesEnabled && activeQuery.isPending);
  const hasError = !!activeQuery.error;

  const views: Array<{ icon: ReactElement; key: DashboardView; label: string }> = [
    { key: "overview", label: translate("home.tabs.overview"), icon: <ApartmentRounded fontSize="small" /> },
    {
      key: "practitioners",
      label: translate("home.tabs.practitioners"),
      icon: <PeopleAltRounded fontSize="small" />,
    },
    { key: "patients", label: translate("home.tabs.patients"), icon: <ElderlyRounded fontSize="small" /> },
    { key: "devices", label: translate("home.tabs.devices"), icon: <DevicesRounded fontSize="small" /> },
  ];

  const rows = useMemo<DashboardRow[]>(() => {
    if (activeView === "overview") {
      return (allLocations.data || []).map(location => {
        return {
          id: location.id,
          resource: ResourceName.Location,
          title: location.name,
          subtitle: location.type
            ? translate(`locationType.${location.type}`, { _: translate("home.rows.location") })
            : translate("home.rows.location"),
          meta: translate("home.rows.locationCounts", {
            patients: location.patientCount,
            practitioners: location.practitionerCount,
            devices: location.deviceCount,
          }),
          icon: <ApartmentRounded />,
          iconBackground: color.primaryTint,
        };
      });
    }

    if (activeView === "practitioners") {
      return (practitioners.data || []).map(practitioner => ({
        id: practitioner.id,
        resource: ResourceName.Practitioner,
        title: practitioner.name,
        subtitle:
          practitioner.locationScope === PRACTITIONER_LOCATION_SCOPE.All
            ? translate("home.rows.allLocations")
            : practitioner.locations.map(item => item.name).join(", ") || translate("home.rows.noAssignment"),
        meta: translate(`practitionerStatus.${practitioner.status}`, { _: String(practitioner.status) }),
        icon: <PeopleAltRounded />,
        iconBackground: color.secondaryTint,
      }));
    }

    if (activeView === "patients") {
      return (patients.data || []).map(patient => ({
        id: patient.id,
        resource: ResourceName.Patient,
        title: patient.name,
        subtitle: patientLocationName(patient) || translate("home.rows.noAssignment"),
        meta: patient.patientNumber
          ? translate("home.rows.patientNumber", { number: patient.patientNumber })
          : translate("home.rows.noPatientNumber"),
        icon: <ElderlyRounded />,
        iconBackground: color.seniorTint,
      }));
    }

    return (devices.data || []).map(device => {
      const statusStyle = deviceStatusStyle[device.status.toLowerCase() as DeviceStatus] ?? deviceStatusStyle.unknown;
      return {
        id: device.id,
        resource: ResourceName.Device,
        title: device.name || device.model || translate("home.rows.unnamedDevice"),
        subtitle: device.location?.name || device.model || translate("home.rows.noAssignment"),
        meta: device.appVersion
          ? translate("home.rows.appVersion", { version: device.appVersion })
          : translate("home.rows.unknownVersion"),
        badge: translate(`deviceStatus.${device.status.toUpperCase()}`, { _: device.status }),
        badgeColor: statusStyle.color,
        badgeBackground: statusStyle.bg,
        icon: <DevicesRounded />,
        iconBackground: color.secondaryTint,
      };
    });
  }, [activeView, allLocations.data, devices.data, patients.data, practitioners.data, translate]);

  const activeResource =
    activeView === "overview"
      ? ResourceName.Location
      : activeView === "practitioners"
        ? ResourceName.Practitioner
        : activeView === "patients"
          ? ResourceName.Patient
          : ResourceName.Device;
  const activeResourceLabel =
    activeView === "overview"
      ? translate(locationId ? "resources.Location" : "resources.LocationSites")
      : translate(`resources.${activeResource}`);

  const greetingName = firstNameFrom(identity);
  const formattedDate = useMemo(() => {
    const date = new Intl.DateTimeFormat("de-DE", {
      weekday: "long",
      day: "numeric",
      month: "long",
      year: "numeric",
    }).format(new Date());
    return date.charAt(0).toUpperCase() + date.slice(1);
  }, []);

  const stats: Array<{
    background: string;
    color: string;
    label: string;
    value: number;
    view: DashboardView;
    query: DashboardQuery<unknown>;
  }> = [
    {
      view: "overview",
      value: counts.locations,
      label: translate(locationId ? "home.stats.areas" : "home.stats.locations"),
      background: color.primarySurface,
      color: color.primary,
      query: locationTotal,
    },
    {
      view: "practitioners",
      value: counts.practitioners,
      label: translate("home.stats.practitioners"),
      background: color.background2,
      color: color.typographyBase,
      query: practitionerTotal,
    },
    {
      view: "patients",
      value: counts.patients,
      label: translate("home.stats.patients"),
      background: color.seniorSurface,
      color: color.seniorAccent,
      query: patientTotal,
    },
    {
      view: "devices",
      value: counts.devices,
      label: translate("home.stats.devices"),
      background: color.secondaryTint,
      color: color.secondary,
      query: deviceTotal,
    },
  ];

  return (
    <Box data-testid="home-dashboard" sx={{ mt: { xs: 0, sm: `-${space.xxl1}px` }, pb: `${space.xxl1}px` }}>
      <Box
        sx={{
          alignItems: { xs: "flex-start", sm: "center" },
          display: "flex",
          flexDirection: { xs: "column", sm: "row" },
          gap: `${space.xl}px`,
          justifyContent: "space-between",
          mb: `${space.xxl}px`,
        }}
      >
        <Box>
          <Typography
            component="h1"
            sx={{
              color: color.typographyBase,
              fontSize: { xs: 24, sm: typography.fontSize.pageHeading },
              fontWeight: typography.fontWeight.bold,
            }}
          >
            {greetingName ? translate("home.welcome", { name: greetingName }) : translate("home.welcomeGeneric")}
          </Typography>
          <Typography
            sx={{
              color: color.secondary40,
              fontSize: typography.fontSize.sm,
              fontWeight: typography.fontWeight.medium,
              mt: `${space.sm}px`,
            }}
          >
            {formattedDate} · {translate(locationId ? "home.scope.selected" : "home.scope.all")}
          </Typography>
        </Box>
        <Button
          data-testid="home-open-onboarding"
          onClick={() => redirect("/onboarding")}
          startIcon={<RocketLaunchRounded />}
          variant="contained"
        >
          {translate("home.openOnboarding")}
        </Button>
      </Box>

      {!isOrganizationLoading && !organizationId && (
        <Alert severity="warning" sx={{ mb: `${space.xxl}px` }}>
          {translate("home.noOrganization")}
        </Alert>
      )}
      {hasError && (
        <Alert severity="error" sx={{ mb: `${space.xxl}px` }}>
          {translate("home.loadError")}
        </Alert>
      )}

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "minmax(0, 1fr)",
          gap: `${space.xxl}px`,
          alignItems: "start",
        }}
      >
        <Paper
          elevation={0}
          sx={{
            border: `1px solid ${color.panelBorder}`,
            borderRadius: `${borderRadius.panel}px`,
            boxShadow: shadows.card,
            p: { xs: `${space.xl}px`, sm: `${space.xxl}px` },
          }}
        >
          <Stack
            direction={{ xs: "column", md: "row" }}
            justifyContent="space-between"
            gap={`${space.xl}px`}
            mb={`${space.xxl}px`}
          >
            <Typography
              component="h2"
              sx={{
                color: color.typographyBase,
                fontSize: typography.fontSize.sectionHeading,
                fontWeight: typography.fontWeight.bold,
              }}
            >
              {translate("home.currentState")}
            </Typography>
            <Box
              sx={{
                display: "flex",
                flexWrap: "wrap",
                gap: `${space.xs}px`,
                bgcolor: color.background2,
                borderRadius: `${borderRadius.md}px`,
                p: `${space.sm}px`,
              }}
            >
              {views.map(view => {
                const isActive = activeView === view.key;
                return (
                  <ButtonBase
                    key={view.key}
                    aria-pressed={isActive}
                    onClick={() => selectView(view.key)}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: `${space.sm2}px`,
                      borderRadius: `${borderRadius.sm2}px`,
                      color: isActive ? color.typographyBase : color.gray,
                      bgcolor: isActive ? color.white : color.transparent,
                      boxShadow: isActive ? shadows.card : shadows.none,
                      fontSize: typography.fontSize.caption,
                      fontWeight: typography.fontWeight.semibold,
                      px: `${space.lg}px`,
                      py: `${space.sm2}px`,
                    }}
                  >
                    {view.icon}
                    {view.label}
                  </ButtonBase>
                );
              })}
            </Box>
          </Stack>

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "minmax(0, 1fr)",
                sm: "repeat(2, minmax(0, 1fr))",
                md: "repeat(4, minmax(0, 1fr))",
              },
              gap: `${space.lg}px`,
            }}
          >
            {stats.map(stat => (
              <ButtonBase
                key={stat.view}
                onClick={() => selectView(stat.view)}
                sx={{
                  alignItems: "flex-start",
                  bgcolor: stat.background,
                  border: `2px solid ${activeView === stat.view ? stat.color : color.transparent}`,
                  borderRadius: `${borderRadius.md}px`,
                  flexDirection: "column",
                  minHeight: 82,
                  p: `${space.lg}px`,
                  textAlign: "left",
                }}
              >
                <Typography
                  sx={{
                    color: stat.color,
                    fontSize: typography.fontSize.metric,
                    fontWeight: typography.fontWeight.bold,
                    lineHeight: 1,
                  }}
                >
                  {stat.query.isPending || stat.query.error ? "–" : stat.value}
                </Typography>
                <Typography
                  sx={{
                    color: color.typographyBase,
                    fontSize: typography.fontSize.caption,
                    fontWeight: typography.fontWeight.semibold,
                    mt: `${space.sm2}px`,
                  }}
                >
                  {stat.label}
                </Typography>
              </ButtonBase>
            ))}
          </Box>

          <Typography
            sx={{
              color: color.secondary40,
              display: "block",
              fontSize: typography.fontSize.caption,
              fontWeight: typography.fontWeight.semibold,
              mt: `${space.xxl}px`,
              mb: `${space.sm}px`,
            }}
          >
            {translate(`home.listTitles.${activeView}`)}
          </Typography>

          {isPending && (
            <Box sx={{ display: "flex", justifyContent: "center", py: `${space.xxxl}px` }}>
              <CircularProgress size={28} />
            </Box>
          )}
          {!isPending && !hasError && rows.length === 0 && (
            <Typography sx={{ color: color.secondary40, fontSize: typography.fontSize.xs, py: `${space.xxl1}px` }}>
              {translate(`home.empty.${activeView}`)}
            </Typography>
          )}
          {!isPending &&
            !hasError &&
            rows.map(row => (
              <ButtonBase
                key={`${row.resource}-${row.id}`}
                onClick={() => redirect(`/${row.resource}/${row.id}/show`)}
                sx={{
                  alignItems: "center",
                  borderBottom: `1px solid ${color.panelBorder}`,
                  borderRadius: `${borderRadius.sm2}px`,
                  display: "flex",
                  gap: `${space.xl}px`,
                  px: `${space.md}px`,
                  py: `${space.lg}px`,
                  textAlign: "left",
                  width: "100%",
                  "&:hover": { bgcolor: color.background2 },
                }}
              >
                <Box
                  sx={{
                    alignItems: "center",
                    bgcolor: row.iconBackground,
                    borderRadius: `${borderRadius.sm2}px`,
                    color: color.primary,
                    display: "flex",
                    flexShrink: 0,
                    height: 42,
                    justifyContent: "center",
                    width: 42,
                  }}
                >
                  {row.icon}
                </Box>
                <Box sx={{ minWidth: 0 }}>
                  <Typography
                    noWrap
                    sx={{
                      color: color.typographyBase,
                      fontSize: typography.fontSize.sm,
                      fontWeight: typography.fontWeight.semibold,
                    }}
                  >
                    {row.title}
                  </Typography>
                  <Typography noWrap sx={{ color: color.secondary40, fontSize: typography.fontSize.caption }}>
                    {row.subtitle}
                  </Typography>
                </Box>
                <Box
                  sx={{
                    alignItems: "flex-end",
                    display: "flex",
                    flexDirection: "column",
                    gap: `${space.sm}px`,
                    ml: "auto",
                  }}
                >
                  <Typography
                    noWrap
                    sx={{
                      color: color.gray,
                      fontSize: typography.fontSize.caption,
                      fontWeight: typography.fontWeight.medium,
                    }}
                  >
                    {row.meta}
                  </Typography>
                  {row.badge && (
                    <Chip
                      label={row.badge}
                      size="small"
                      sx={{
                        bgcolor: row.badgeBackground,
                        color: row.badgeColor,
                        fontSize: typography.fontSize.xxs,
                        fontWeight: typography.fontWeight.semibold,
                        height: 24,
                      }}
                    />
                  )}
                </Box>
              </ButtonBase>
            ))}

          {!isPending && !hasError && rows.length > 0 && (
            <ListPaginationContext.Provider
              value={{
                page,
                perPage,
                setPage,
                setPerPage: changePerPage,
                total: activeQuery.total,
                hasPreviousPage: page > 1,
                hasNextPage: page * perPage < (activeQuery.total || 0),
                isPending,
                isLoading: isPending,
                resource: activeResource,
              }}
            >
              <Pagination rowsPerPageOptions={[5, 10, 25, 50]} />
            </ListPaginationContext.Provider>
          )}

          {!isPending && !hasError && rows.length > 0 && (
            <Button
              endIcon={<ArrowForwardRounded />}
              fullWidth
              onClick={() => redirect(`/${activeResource}`)}
              sx={{
                bgcolor: color.background2,
                color: color.primary,
                fontWeight: typography.fontWeight.semibold,
                mt: `${space.xl}px`,
                py: `${space.lg}px`,
              }}
            >
              {translate("home.showAll", { resource: activeResourceLabel })}
            </Button>
          )}
        </Paper>
      </Box>
    </Box>
  );
};
